/**
 * Service Parents - API Tracking
 * GET/POST /api/v1/parents/ , GET/PUT/PATCH/DELETE /api/v1/parents/{id}/
 */
import { createTrackingClient } from "../trackingApi";

export interface TrackingParent {
  id: string;
  user_id: number | null;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  institution_id: number | null;
  institution_name: string | null;
  sub_area_id: number | null;
  sub_area_name: string | null;
  children_count: number;
}

export interface ParentCreatePayload {
  user_id: number;
  institution_id?: number | null;
  sub_area_id?: number | null;
}

export interface ParentUpdatePayload {
  institution_id?: number | null;
  sub_area_id?: number | null;
}

function getClient(accessToken: string | undefined) {
  return createTrackingClient(accessToken);
}

export async function fetchTrackingParents(
  accessToken: string | undefined
): Promise<TrackingParent[]> {
  const client = getClient(accessToken);
  const { data } = await client.get<TrackingParent[] | { results: TrackingParent[] }>("/api/v1/parents/");
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && "results" in data) return (data as { results: TrackingParent[] }).results;
  return [];
}

export async function createTrackingParent(
  accessToken: string | undefined,
  payload: ParentCreatePayload
): Promise<TrackingParent> {
  const client = getClient(accessToken);
  const { data } = await client.post<TrackingParent>("/api/v1/parents/", payload);
  return data;
}

export async function updateTrackingParent(
  accessToken: string | undefined,
  id: string,
  payload: ParentUpdatePayload
): Promise<TrackingParent> {
  const client = getClient(accessToken);
  const { data } = await client.put<TrackingParent>(`/api/v1/parents/${id}/`, payload);
  return data;
}

export async function patchTrackingParent(
  accessToken: string | undefined,
  id: string,
  payload: Partial<ParentUpdatePayload>
): Promise<TrackingParent> {
  const client = getClient(accessToken);
  const { data } = await client.patch<TrackingParent>(`/api/v1/parents/${id}/`, payload);
  return data;
}

export async function deleteTrackingParent(
  accessToken: string | undefined,
  id: string
): Promise<void> {
  const client = getClient(accessToken);
  await client.delete(`/api/v1/parents/${id}/`);
}
